import type { CartAction, CartItem, CartMoney, CartState } from "./types";
import { addMoneyAmounts, initialCartState } from "./types";

function clampQty(qty: number, maxQty?: number): number {
  const safeQty = Number.isFinite(qty) ? Math.floor(qty) : 0;
  const min = 1;
  const max =
    typeof maxQty === "number" && Number.isFinite(maxQty)
      ? Math.max(maxQty, min)
      : Infinity;
  return Math.min(Math.max(safeQty, min), max);
}

function addMoney(a?: CartMoney, b?: CartMoney): CartMoney | undefined {
  if (!a && !b) return undefined;
  const currency = a?.currency || b?.currency || "EUR";

  // Enforce single currency - prevent cross-currency mixing
  if (a && b && a.currency !== b.currency) {
    // Currency mismatch logged via logger if needed
  }

  const amount = addMoneyAmounts(a?.amount || 0, b?.amount || 0);
  return { amount, currency };
}

// Generate merge key that includes variant attributes to prevent collapsing distinct variants
function getMergeKey(item: CartItem): string {
  const variantKey = item.variant
    ? Object.entries(item.variant)
        .sort()
        .map(([k, v]) => `${k}:${v}`)
        .join("|")
    : "";
  const skuKey = item.sku || "";
  return `${item.id}${skuKey ? `:${skuKey}` : ""}${
    variantKey ? `:${variantKey}` : ""
  }`;
}

// Check if two items can be merged (same product, same variant)
function canMergeItems(item1: CartItem, item2: CartItem): boolean {
  return getMergeKey(item1) === getMergeKey(item2);
}

export function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD_ITEM": {
      const { item, mergeById } = action.payload;
      const qty = clampQty(item.qty, item.maxQty);

      if (mergeById) {
        // Find item with same merge key (includes variant/SKU)
        const idx = state.items.findIndex((i) => canMergeItems(i, item));
        if (idx >= 0) {
          const existing = state.items[idx];
          const mergedQty = clampQty(
            existing.qty + qty,
            existing.maxQty ?? item.maxQty
          );
          // Preserve taxRatePercent if present on either; prefer existing when both exist
          const mergedTax =
            typeof existing.taxRatePercent === "number"
              ? existing.taxRatePercent
              : item.taxRatePercent;
          const updated: CartItem = {
            ...existing,
            qty: mergedQty,
          };
          if (mergedTax !== undefined) {
            updated.taxRatePercent = mergedTax;
          }
          const items = state.items.slice();
          items[idx] = updated;
          return { ...state, items };
        }
      }
      return { ...state, items: [...state.items, { ...item, qty }] };
    }
    case "REMOVE_ITEM": {
      const { id } = action.payload;
      return { ...state, items: state.items.filter((i) => i.id !== id) };
    }
    case "UPDATE_QTY": {
      const { id, qty } = action.payload;
      return {
        ...state,
        items: state.items.map((i) =>
          i.id === id ? { ...i, qty: clampQty(qty, i.maxQty) } : i
        ),
      };
    }
    case "CLEAR_CART": {
      return { ...initialCartState };
    }
    case "MOVE_TO_SAVED": {
      const { id } = action.payload;
      const item = state.items.find((i) => i.id === id);
      if (!item) return state;
      return {
        ...state,
        items: state.items.filter((i) => i.id !== id),
        savedForLater: [...state.savedForLater, item],
      };
    }
    case "MOVE_TO_CART": {
      const { id } = action.payload;
      const item = state.savedForLater.find((i) => i.id === id);
      if (!item) return state;

      // Check if item already exists in cart and merge if possible
      const existingIdx = state.items.findIndex((i) => canMergeItems(i, item));
      if (existingIdx >= 0) {
        const existing = state.items[existingIdx];
        const mergedQty = clampQty(
          existing.qty + item.qty,
          existing.maxQty ?? item.maxQty
        );
        const mergedTax =
          typeof existing.taxRatePercent === "number"
            ? existing.taxRatePercent
            : item.taxRatePercent;
        const updated: CartItem = {
          ...existing,
          qty: mergedQty,
        };
        if (mergedTax !== undefined) {
          updated.taxRatePercent = mergedTax;
        }
        const items = state.items.slice();
        items[existingIdx] = updated;

        return {
          ...state,
          savedForLater: state.savedForLater.filter((i) => i.id !== id),
          items,
        };
      }

      return {
        ...state,
        savedForLater: state.savedForLater.filter((i) => i.id !== id),
        items: [
          ...state.items,
          { ...item, qty: clampQty(item.qty, item.maxQty) },
        ],
      };
    }
    case "SET_NOTES": {
      const { id, notes } = action.payload;
      return {
        ...state,
        items: state.items.map((i) => (i.id === id ? { ...i, notes } : i)),
      };
    }
    case "MERGE_CART": {
      const { serverCart, policy = "merge" } = action.payload;
      if (policy === "replace") {
        return serverCart;
      }
      // For merge, combine items from both carts
      const mergedItems = [...state.items];
      serverCart.items.forEach((serverItem) => {
        const existingIdx = mergedItems.findIndex((i) => canMergeItems(i, serverItem));
        if (existingIdx >= 0) {
          // Merge quantities
          const existing = mergedItems[existingIdx];
          const mergedQty = clampQty(
            existing.qty + serverItem.qty,
            existing.maxQty ?? serverItem.maxQty
          );
          const updated: CartItem = {
            ...existing,
            qty: mergedQty,
          };
          if (serverItem.taxRatePercent !== undefined) {
            updated.taxRatePercent = serverItem.taxRatePercent;
          }
          mergedItems[existingIdx] = updated;
        } else {
          mergedItems.push(serverItem);
        }
      });
      return {
        ...state,
        items: mergedItems,
        savedForLater: [...state.savedForLater, ...serverCart.savedForLater],
      };
    }
    case "LOAD_STATE": {
      return action.payload;
    }
    default:
      return state;
  }
}

export function calculateSubtotal(items: CartItem[]): CartMoney {
  if (items.length === 0) {
    return { amount: 0, currency: "EUR" };
  }

  // Ensure all items have the same currency
  const cartCurrency = items[0].price.currency;
  const hasMixedCurrencies = items.some(
    (item) => item.price.currency !== cartCurrency
  );

  if (hasMixedCurrencies) {
    console.warn(
      `Mixed currencies detected in cart. Using ${cartCurrency} as primary.`
    );
  }

  return items.reduce<CartMoney>(
    (acc, item) => {
      // Use precise money math to avoid floating point errors
      const lineAmount =
        addMoneyAmounts(0, item.price.amount || 0) * (item.qty || 0);
      const line: CartMoney = {
        amount: lineAmount,
        currency: cartCurrency, // Use cart currency consistently
      };
      const sum = addMoney(acc, line)!;
      return sum;
    },
    { amount: 0, currency: cartCurrency }
  );
}
